// The fan dial, drawing only — a 270° arc with the gap at the BOTTOM (starts at
// 135° down-left, sweeps clockwise to 45° down-right), the spinning blades in
// the middle, and the speed readout beneath the hub. Port of the Dart's
// fanTouchScreen visuals.
//
// EXTRACTED SO THERE IS ONE COPY. Two screens draw this now — the full fan
// screen (DeviceID 53 over MQTT, draggable) and the BLE bench panel (the fan
// channels of a 7+1 / 7+2, read-only). The relay tiles already taught this
// lesson: buttonLayout.js exists because the mobile screen and the desktop card
// each held their own copy of the button order and drifted apart. A dial is
// more geometry than that, not less.
//
// Drawing only, deliberately: pointer handling, the dead hub, drag-then-commit
// and what a gesture publishes all stay with the screen that owns the device.
// This takes a speed and paints it.

import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import SpinningFan from './SpinningFan';

export const DIAL_START = (3 * Math.PI) / 4;   // 135°
export const DIAL_SWEEP = (3 * Math.PI) / 2;   // 270°

// SVG arc from `from` to `from + sweep` on a circle of radius r centred at c.
export function arcPath(cx, cy, r, from, sweep) {
  const x1 = cx + r * Math.cos(from);
  const y1 = cy + r * Math.sin(from);
  const x2 = cx + r * Math.cos(from + sweep);
  const y2 = cy + r * Math.sin(from + sweep);
  const large = sweep > Math.PI ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
}

/**
 * @param size      px, square
 * @param frac      0..1 — where the knob sits and how fast the blades turn
 * @param running   blades turning and coloured live
 * @param known     false paints the "no status yet" blue, as the relay tiles do
 * @param stepLabel big caption under the hub, e.g. "3" / "Max"
 * @param statusLabel small caption under that, e.g. "Medium"
 */
export default function FanGauge({
  size, frac, running, known, stepLabel, statusLabel,
}) {
  const theme = useTheme();
  const c = theme.sli;

  const r = size / 2;
  const stroke = size * 0.055;
  const radius = (size - stroke) / 2 - size * 0.02;
  const knobAngle = DIAL_START + DIAL_SWEEP * frac;
  // Blue for unknown is not decoration — it is the same rule the relay tiles
  // follow, so "nothing has reported yet" never reads as "off".
  const statusColour = !known ? '#2196F3' : (running ? '#66FF00' : '#F44336');

  return (
    <>
      <svg width={size} height={size} style={{ position: 'absolute', inset: 0 }}>
        <path
          d={arcPath(r, r, radius, DIAL_START, DIAL_SWEEP)}
          stroke={`${c.text}2E`} strokeWidth={stroke} strokeLinecap="round" fill="none"
        />
        {frac > 0 && (
          <>
            <path
              d={arcPath(r, r, radius, DIAL_START, DIAL_SWEEP * frac)}
              stroke={running ? c.button : '#9E9E9E'} strokeWidth={stroke * 1.9}
              strokeLinecap="round" fill="none" opacity={0.35}
              style={{ filter: 'blur(8px)' }}
            />
            <path
              d={arcPath(r, r, radius, DIAL_START, DIAL_SWEEP * frac)}
              stroke={running ? c.button : '#9E9E9E'} strokeWidth={stroke}
              strokeLinecap="round" fill="none"
            />
          </>
        )}
        <circle
          cx={r + radius * Math.cos(knobAngle)} cy={r + radius * Math.sin(knobAngle)}
          r={stroke * 0.85} fill="rgba(255,255,255,0.95)"
        />
        <circle
          cx={r + radius * Math.cos(knobAngle)} cy={r + radius * Math.sin(knobAngle)}
          r={stroke * 0.52} fill={statusColour}
        />
      </svg>

      <Box sx={{
        position: 'absolute', inset: 0, display: 'flex',
        alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
      }}
      >
        <SpinningFan
          size={size * 0.58}
          speedFraction={running ? frac : 0}
          bladeColor={c.button}
          idleColor="#757575"
          hubColor={statusColour}
        />
      </Box>

      <Box sx={{
        position: 'absolute', bottom: size * 0.14, left: 0, right: 0,
        textAlign: 'center', pointerEvents: 'none',
      }}
      >
        <Typography sx={{
          fontSize: Math.min(44, Math.max(16, size * 0.13)),
          fontWeight: 700, color: c.text, lineHeight: 1.1,
        }}
        >
          {stepLabel}
        </Typography>
        <Typography sx={{
          fontSize: Math.min(18, Math.max(10, size * 0.052)),
          color: `${c.text}BF`, letterSpacing: '1.1px',
        }}
        >
          {known ? statusLabel : 'Unknown'}
        </Typography>
      </Box>
    </>
  );
}
